import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { UserProfile } from 'oidc-client-ts'
import { useEffect, useState } from 'react'
import { useStoreActions, useStoreState } from 'store/hooks'
import getEnvConfig from 'utils/getEnvConfig'
import { ensureSignedIn } from './Auth'

export interface AuthWrapperProps {
    children: JSX.Element
}

const AuthWrapper = (props: AuthWrapperProps): JSX.Element => {
    const env = getEnvConfig()

    const loggedInUser = useStoreState((state) => state.model.loggedInUser)
    const loadCMSData = useStoreActions((actions) => actions.model.loadCMSData)

    const [authIsLoading, setAuthIsLoading] = useState<boolean>(env.Auth.Enabled)
    const [authError, setAuthError] = useState<string | null>(null)
    const [profile, setProfile] = useState<UserProfile | null>(null)

    useEffect(() => {
        if (env.Auth.Enabled) {
            ensureSignedIn()
                .then((user) => {
                    setProfile(user.profile)
                    setAuthIsLoading(false)
                })
                .catch((e) => {
                    console.error('Failed to sign in:', e)
                    setAuthError(e instanceof Error ? e.message : String(e))
                    setAuthIsLoading(false)
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Load CMS Data
    useEffect(() => {
        if (!loggedInUser) {
            if (env.Auth.Enabled && !authIsLoading && profile) {
                // NOTE: the server ignores these arguments and uses the verified token claims;
                // they are only kept for schema compatibility
                loadCMSData({
                    email: profile.email || '',
                    firstName: profile.given_name || '',
                    lastName: profile.family_name || '',
                })
            } else if (!env.Auth.Enabled) {
                loadCMSData({
                    email: '',
                    firstName: '',
                    lastName: '',
                })
            }
        }
    }, [loadCMSData, loggedInUser, env.Auth.Enabled, profile, authIsLoading])

    if (authIsLoading) {
        return (
            <Box width={'100vw'} height={'100vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <CircularProgress />
            </Box>
        )
    } else if (authError) {
        return (
            <Box
                width={'100vw'}
                height={'100vh'}
                display={'flex'}
                flexDirection={'column'}
                gap={2}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Typography variant="h6">Login failed</Typography>
                <Typography color="text.secondary" maxWidth={600} textAlign="center">
                    {authError}
                </Typography>
                <Button variant="contained" onClick={() => window.location.assign('/')}>
                    Try again
                </Button>
            </Box>
        )
    } else {
        return props.children
    }
}

export default AuthWrapper
